import { EventEmitter } from 'events';
import ws from 'websocket';

import { reportException, Trace } from '@gitbook/util-debug';
import type { MaybePromise } from '@gitbook/util-ts';

export * from './ConnectionPool';

export enum WebSocketState {
    CONNECTING = 'connecting',
    OPEN = 'open',
    CLOSED = 'closed',
    ERRORED = 'errored',
}

export type WebSocketMessage = Uint8Array | string;
export type WebSocketType = typeof ws.w3cwebsocket;

/**
 * Application-level heartbeat configuration. A dead TCP connection (computer slept, NAT/proxy
 * dropped the idle flow) leaves the browser socket in an OPEN state forever with no event fired;
 * probing is the only way to detect it. Opt-in because the server must answer the
 * {@link HEARTBEAT_PING_MESSAGE} probe with {@link HEARTBEAT_PONG_MESSAGE}.
 */
export interface WebSocketHeartbeatOptions {
    /**
     * How often to probe an open connection. Default: 25 seconds.
     */
    intervalMs?: number;

    /**
     * How long to wait after a probe for any inbound traffic before declaring the connection
     * dead and recycling it. Default: 10 seconds.
     */
    timeoutMs?: number;
}

/**
 * Configuration options for the WebSocket.
 */
export interface WebSocketOptions {
    /**
     * Optionally override the WebSocket implementation to use.
     */
    wsImpl?: WebSocketType;

    /**
     * If true, reconnections are attempted. Default: true.
     */
    attemptReconnect?: boolean;

    /**
     * Probe open connections to detect half-open sockets that would otherwise look OPEN
     * forever. Also makes {@link WebSocket.reconnectNow} meaningful on an OPEN socket: it
     * probes immediately instead of doing nothing. Pass `true` to enable with the default
     * messages and timings.
     */
    heartbeat?: true | WebSocketHeartbeatOptions;

    /**
     * Maximum duration of a single connection attempt, including resolving the socket URL
     * (which may hang on a dead network, e.g. an auth token fetch right after wake-up).
     * When exceeded, the attempt is abandoned and a reconnection is scheduled. Opt-in: when
     * unset, an attempt can wait on the URL resolution indefinitely.
     */
    connectTimeoutMs?: number;

    /**
     * Decide whether a closed connection is worth reopening, from the close frame that ended
     * it. Called once per close, before any reconnection is scheduled, and only for a
     * connection that had started opening — so it always describes the attempt that just
     * failed, unlike {@link WebSocket.lastClose}, which a caller reading it later can find
     * holding an older connection's frame.
     *
     * Return false for a close the same attempt would only earn again — a server refusing the
     * request rather than dropping it — and the socket stays closed until something asks for it
     * again. Transport failures that produce no close frame (a failed URL resolution, the
     * connect watchdog, an error event) never consult this and always reconnect.
     *
     * Default: every close is reconnected.
     */
    shouldReconnect?: (close: { code: number; reason: string }) => boolean;

    /**
     * If false, messages sent while the socket is not open are dropped instead of being
     * buffered and replayed on the next connection. Protocols that keep per-connection state
     * (e.g. an RPC session) must not replay frames from a previous session into a fresh
     * connection. Default: true.
     */
    queueMessagesWhileClosed?: boolean;
}

/**
 * The heartbeat probe and its expected answer. Deliberately not configurable: every server
 * answering heartbeats (durable object socket auto-responses) speaks this exact pair.
 */
export const HEARTBEAT_PING_MESSAGE = 'ping';
export const HEARTBEAT_PONG_MESSAGE = 'pong';

/**
 * How long a connection has to last to count as one that worked.
 *
 * Opening proves the handshake was answered, not that the connection is usable: a server that
 * accepts and immediately closes — refusing with a reason, or restarting — opens just as
 * convincingly. Resetting the backoff on that alone lets a connection that fails the same way
 * every time retry at the shortest delay forever, which is the opposite of what a backoff is for.
 */
const STABLE_CONNECTION_MS = 10_000;

const DEFAULT_HEARTBEAT_INTERVAL_MS = 25_000;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 10_000;

/**
 * Websocket wrapper with automatic reconnection.
 */
export class WebSocket {
    public state: WebSocketState = WebSocketState.CLOSED;
    public webSocket: ws.w3cwebsocket | undefined;

    /**
     * Close event of the last connection that ended with a socket-level close. Lets protocols
     * on top of the socket react to server-signalled close codes (e.g. a "session expired"
     * reset guaranteeing that in-flight frames were not processed). Cleared when a
     * connection opens.
     */
    public lastClose: { code: number; reason: string } | null = null;

    private options: WebSocketOptions;
    private trace: Trace | null = null;
    private destroyed = false;
    private getSocketURL: () => MaybePromise<URL>;

    private openBackoffDelay = 1000;
    private openBackoffTimer: NodeJS.Timeout | undefined;

    /** When the current connection opened, to tell a working connection from one that just began. */
    private openedAt: number | undefined;

    /**
     * Identifies the current open() attempt. close(), destroy() and the connect watchdog bump
     * it so a stale in-flight attempt (e.g. a slow socket URL resolution) abandons instead of
     * reviving a connection that was closed under it.
     */
    private openAttempt = 0;

    /**
     * Heartbeat configuration with defaults applied, or null when disabled. Resolved once so
     * the probing code doesn't re-apply defaults on every use.
     */
    private heartbeat: Required<WebSocketHeartbeatOptions> | null;
    private heartbeatTimer: NodeJS.Timeout | undefined;
    private heartbeatDeadline: NodeJS.Timeout | undefined;

    private pendingMessages: WebSocketMessage[] = [];

    private events = new EventEmitter();

    constructor(getSocketURL: () => MaybePromise<URL>, options: WebSocketOptions = {}) {
        this.getSocketURL = getSocketURL;
        this.options = options;
        this.heartbeat = options.heartbeat
            ? {
                  intervalMs: DEFAULT_HEARTBEAT_INTERVAL_MS,
                  timeoutMs: DEFAULT_HEARTBEAT_TIMEOUT_MS,
                  ...(options.heartbeat === true ? {} : options.heartbeat),
              }
            : null;
    }

    /**
     * Add a listener to be called when a message is received.
     */
    public async open(): Promise<void> {
        this.assertNotDestroyed();

        if (this.webSocket) {
            throw new Error('WebSocket already opened');
        }

        const attempt = ++this.openAttempt;
        const abandoned = () => this.destroyed || attempt !== this.openAttempt;

        // Belongs to the connection being replaced, not to this attempt.
        this.openedAt = undefined;

        // Belongs to the connection being replaced, not to this attempt. Cleared here rather
        // than only on a successful open, so a reader after a failed attempt cannot mistake the
        // previous connection's close for this one's.
        this.lastClose = null;

        this.trace = new Trace('websocket', { sampleRate: 0 });
        this.clearScheduledReconnection();
        this.setState(WebSocketState.CONNECTING);

        // Watchdog over the whole attempt (URL resolution + handshake): without it a hanging
        // URL resolution leaves the socket in CONNECTING forever, a state neither the
        // reconnection backoff nor reconnectNow() can recover from.
        let rejectOnTimeout: ((error: Error) => void) | undefined;
        let timeoutPromise: Promise<void> | undefined;
        let connectTimeout: NodeJS.Timeout | undefined;
        if (this.options.connectTimeoutMs) {
            timeoutPromise = new Promise<void>((_resolve, reject) => {
                rejectOnTimeout = reject;
            });
            connectTimeout = setTimeout(() => {
                // An abandoned attempt is no longer this socket's to tear down — a newer one
                // owns it — but it still has a caller awaiting it, so it must be settled either
                // way. Leaving it pending strands every caller that shares the promise.
                if (!abandoned()) {
                    this.trace?.logger.error('WebSocket connection attempt timed out');
                    this.closeAndScheduleReconnection(WebSocketState.ERRORED);
                }

                rejectOnTimeout?.(new Error('WebSocket connection attempt timed out'));
            }, this.options.connectTimeoutMs);
        }

        const openAttemptPromise = this.trace.wrap(async () => {
            let url: URL;
            try {
                url = await this.getSocketURL();
            } catch (error) {
                // A failed URL resolution (e.g. an auth token fetch) happens before any browser
                // socket exists, so no onclose/onerror can fire to schedule a retry. Recover
                // like any other connection failure instead of stranding the socket in
                // CONNECTING.
                if (!abandoned()) {
                    this.closeAndScheduleReconnection(WebSocketState.ERRORED);
                }
                throw error;
            }

            if (abandoned()) {
                return;
            }

            if (url.protocol !== 'ws:' && url.protocol !== 'wss:') {
                // An invalid scheme is a permanent misconfiguration, not a transient network
                // failure: fail hard without scheduling a reconnection, which would retry the
                // same bad URL forever.
                if (!abandoned()) {
                    this.close(WebSocketState.ERRORED);
                }
                throw new Error(
                    `Failed to construct 'WebSocket': The URL's scheme must be either 'ws' or 'wss': got ${url.toString()}`
                );
            }

            // Headers can't be passed in WebSocket, we need to use the URL
            const headers = this.trace?.headers();
            if (headers) {
                Object.entries(headers).forEach(([key, value]) => {
                    url.searchParams.append(key, value);
                });
            }

            let errored = false;
            return new Promise<void>((resolve, reject) => {
                const socketUrl = url.toString();
                this.trace?.logger.info('opening WebSocket:', socketUrl);

                const WsImpl = this.options.wsImpl ?? ws.w3cwebsocket;
                const webSocket = new WsImpl(socketUrl);
                this.webSocket = webSocket;

                try {
                    webSocket.binaryType = 'arraybuffer';
                } catch (_err) {
                    // Ignore, it throws an error in Node.js
                }

                webSocket.onopen = () => {
                    if (this.destroyed || this.webSocket !== webSocket) {
                        // If the socket was destroyed or replaced before it was opened, we close it
                        webSocket.close();
                        return;
                    }

                    this.trace?.logger.warning('WebSocket opened');

                    // Not the backoff reset: that waits until the connection has lasted long
                    // enough to have been worth opening (see STABLE_CONNECTION_MS).
                    this.openedAt = Date.now();
                    if (this.openBackoffTimer) {
                        clearTimeout(this.openBackoffTimer);
                        this.openBackoffTimer = undefined;
                    }

                    this.lastClose = null;
                    this.setState(WebSocketState.OPEN);
                    this.sendPendingMessages();
                    this.startHeartbeat();
                    resolve();
                };

                webSocket.onclose = (event) => {
                    if (errored) {
                        const reason = getWebSocketCloseReason(event);
                        this.trace?.logger.error(
                            'WebSocket closed with error:',
                            event.code,
                            reason
                        );
                        if (event.code !== 1006) {
                            // Error code 1006 means that the connection was closed abruptly,
                            // which happens when computers go to sleep or the process put to the brackground.
                            // To prevent noise, we don't track them.
                            reportException(
                                new Error(`Websocket error [${event.code}]: ${reason}`)
                            );
                        }
                    }

                    if (this.destroyed || this.webSocket !== webSocket) {
                        // A socket that was already detached (closed manually or replaced)
                        // finished closing: the wrapper has moved on, don't touch its state.
                        return;
                    }

                    this.lastClose = { code: event.code, reason: event.reason };

                    const previousState = this.state;

                    if (previousState === WebSocketState.CONNECTING) {
                        this.trace?.logger.error(
                            'Websocket closed before initial connection',
                            event
                        );
                    }

                    if (previousState === WebSocketState.OPEN) {
                        this.trace?.logger.warning(
                            'WebSocket unexpectedly closed, reconnecting:',
                            event.code,
                            event.reason
                        );
                    }

                    // A handshake that closed cleanly (no onerror fired) must retry like any
                    // other connection failure instead of stranding the socket in CLOSED. A
                    // socket that was already closed stays closed.
                    if (
                        previousState === WebSocketState.CONNECTING ||
                        previousState === WebSocketState.OPEN
                    ) {
                        // Asked with the frame that just arrived, so the answer is about this
                        // connection. A refusal stays closed instead of being reopened on a
                        // backoff that would earn the same refusal.
                        const reconnect =
                            this.options.shouldReconnect?.({
                                code: event.code,
                                reason: event.reason,
                            }) ?? true;

                        if (reconnect) {
                            this.closeAndScheduleReconnection(WebSocketState.CLOSED);
                        } else {
                            this.trace?.logger.warning(
                                'WebSocket closed for good:',
                                event.code,
                                event.reason
                            );
                            this.close(WebSocketState.CLOSED);
                        }
                    } else {
                        this.close(WebSocketState.CLOSED);
                    }

                    if (previousState === WebSocketState.CONNECTING) {
                        // The handshake never completed, so `resolve` above was never reached.
                        // Without this the attempt stays pending forever and every caller
                        // awaiting open() — including ones that share the promise — hangs.
                        reject(
                            new Error(
                                `WebSocket closed before it opened [${event.code}]: ${event.reason}`
                            )
                        );
                    }
                };

                webSocket.onerror = (event) => {
                    errored = true;
                    if (this.destroyed || this.webSocket !== webSocket) {
                        return;
                    }

                    const previousState = this.state;

                    if (previousState === WebSocketState.CONNECTING) {
                        this.trace?.logger.error('Websocket error with initial connection', event);
                    }

                    this.trace?.logger.error('WebSocket error, reconnecting:', event);
                    this.closeAndScheduleReconnection(WebSocketState.ERRORED);

                    if (previousState === WebSocketState.CONNECTING) {
                        // Same as in onclose: an attempt that errored during the handshake never
                        // resolves on its own, and a pending open() promise strands its callers.
                        reject(new Error('WebSocket errored before it opened'));
                    }
                };

                webSocket.onmessage = (event) => {
                    if (this.destroyed || this.webSocket !== webSocket) {
                        // If the websocket was not correctly closed and we still receive message,
                        // we close it again.
                        webSocket.close();
                        return;
                    }

                    // Any inbound traffic proves the connection is alive: settle a pending
                    // heartbeat probe.
                    if (this.heartbeatDeadline) {
                        clearTimeout(this.heartbeatDeadline);
                        this.heartbeatDeadline = undefined;
                    }

                    const data = event.data;

                    // Heartbeat answers are transport-level and never surfaced to listeners.
                    if (this.heartbeat && data === HEARTBEAT_PONG_MESSAGE) {
                        return;
                    }

                    if (data instanceof ArrayBuffer) {
                        this.events.emit('message', new Uint8Array(data));
                    } else if (typeof data === 'string') {
                        this.events.emit('message', data);
                    }
                };
            });
        });

        if (!timeoutPromise) {
            return openAttemptPromise;
        }

        // Once the watchdog has rejected the race, nothing observes the attempt anymore:
        // swallow its late failure so it doesn't surface as an unhandled rejection.
        openAttemptPromise.catch(() => {});

        try {
            return await Promise.race([openAttemptPromise, timeoutPromise]);
        } finally {
            clearTimeout(connectTimeout);
        }
    }

    /**
     * Close the websocket connection, keeping the state and events intact.
     */
    public close(state: WebSocketState.CLOSED | WebSocketState.ERRORED = WebSocketState.CLOSED) {
        this.teardown();
        this.setState(state);
    }

    /**
     * Tear the current connection down without announcing a new state.
     *
     * Kept separate from {@link close} so that a caller which also schedules a reconnection can
     * announce the new state last: a listener reacting to the state change then observes a
     * socket that is fully torn down and already scheduled to reopen, rather than one caught
     * halfway through.
     */
    private teardown() {
        this.assertNotDestroyed();

        // Abandon any in-flight open() attempt so a slow URL resolution can't revive a
        // connection that was closed under it.
        this.openAttempt++;
        this.stopHeartbeat();
        this.clearScheduledReconnection();

        if (this.webSocket) {
            this.trace?.logger.info('Closing WebSocket');
            this.webSocket.close();
            this.webSocket = undefined;
        }
    }

    /**
     * Close the connection and schedule the reopen, announcing the new state last.
     *
     * Every connection failure goes through here, so on a socket that reconnects (see
     * {@link WebSocketOptions.attemptReconnect}) a listener reacting to the state change can rely
     * on the reopen already being scheduled. With reconnection disabled nothing is scheduled and
     * the state change simply reports the failure.
     */
    private closeAndScheduleReconnection(state: WebSocketState.CLOSED | WebSocketState.ERRORED) {
        this.teardown();
        this.scheduleReconnection();
        this.setState(state);
    }

    /**
     * Destroy the socket, making it unusable.
     */
    public destroy() {
        this.close();
        this.events.removeAllListeners();
        this.pendingMessages = [];
        this.destroyed = true;
    }

    /**
     * Check if the socket is destroyed.
     */
    public isDestroyed(): boolean {
        return this.destroyed;
    }

    /**
     * Reopen the websocket after a delay.
     */
    private scheduleReconnection() {
        if (!(this.options.attemptReconnect ?? true)) {
            return;
        }

        if (this.openBackoffTimer) {
            return;
        }

        if (this.webSocket) {
            this.close();
        }

        // A connection that lasted is evidence the delay that produced it was long enough, so the
        // next outage starts over from the shortest one. One that died on arrival is not.
        const openedAt = this.openedAt;
        this.openedAt = undefined;
        if (openedAt !== undefined && Date.now() - openedAt >= STABLE_CONNECTION_MS) {
            this.openBackoffDelay = 1000;
        }

        this.trace?.logger.info('Scheduling reconnection in', this.openBackoffDelay, 'ms');
        this.openBackoffTimer = setTimeout(() => {
            this.open().catch((error) => {
                if (this.destroyed) {
                    throw error;
                }

                this.trace?.logger.error('Failed to reopen socket', error);
                this.trace?.logger.error('Retrying...');
                this.closeAndScheduleReconnection(WebSocketState.ERRORED);
            });
            this.openBackoffDelay = Math.min(this.openBackoffDelay * 2, 30000);
        }, this.openBackoffDelay);
    }

    /**
     * Force the scheduled reopen to happen now.
     */
    public async forceScheduledReconnection(): Promise<void> {
        if (!this.openBackoffTimer) {
            throw new Error('No scheduled reconnection');
        }

        await this.open();
    }

    /**
     * Reconnect immediately, bypassing any pending backoff delay.
     *
     * Unlike {@link forceScheduledReconnection}, this is safe to call at any time:
     * it does nothing if the socket is already connecting or destroyed, and
     * resets the backoff delay so the next attempt happens right away. Useful to
     * recover quickly when the page regains focus after a disconnection.
     *
     * On an OPEN socket with a heartbeat configured, it probes the connection instead:
     * after a wake-from-sleep the socket can be half-open (dead with no close event
     * fired), and the probe recycles it if it turns out to be dead.
     */
    public reconnectNow(): void {
        if (this.destroyed) {
            return;
        }

        if (this.state === WebSocketState.OPEN) {
            this.probeConnection();
            return;
        }

        if (this.state === WebSocketState.CONNECTING) {
            return;
        }

        // Cancel the pending backoff and reset it, so this and any subsequent
        // automatic reconnection starts from the shortest delay again.
        this.clearScheduledReconnection();
        this.openBackoffDelay = 1000;

        this.open().catch((error) => {
            if (this.destroyed) {
                throw error;
            }

            this.trace?.logger.error('Failed to reconnect socket', error);
            this.closeAndScheduleReconnection(WebSocketState.ERRORED);
        });
    }

    /**
     * Clear the scheduled reopen.
     */
    private clearScheduledReconnection() {
        if (this.openBackoffTimer) {
            clearTimeout(this.openBackoffTimer);
            this.openBackoffTimer = undefined;
        }
    }

    /**
     * Tear down the current connection and reconnect with backoff.
     *
     * For connections that look open but are known to be broken — a failed heartbeat, or a
     * stateful protocol session on top of the socket that got out of sync — where
     * {@link close} alone would leave the socket closed for good (manual closes never
     * reconnect on their own).
     */
    public recycle(): void {
        if (this.destroyed) {
            return;
        }

        if (this.state === WebSocketState.CONNECTING) {
            // An attempt is already in flight; let it finish or fail and reschedule itself.
            return;
        }

        if (this.state === WebSocketState.OPEN) {
            this.closeAndScheduleReconnection(WebSocketState.ERRORED);
            return;
        }

        this.scheduleReconnection();
    }

    /**
     * Start probing the open connection at a fixed interval. Only active when a heartbeat is
     * configured; stopped whenever the connection closes.
     */
    private startHeartbeat() {
        const heartbeat = this.heartbeat;
        if (!heartbeat) {
            return;
        }

        this.stopHeartbeat();

        const tick = () => {
            this.heartbeatTimer = setTimeout(() => {
                this.probeConnection();
                tick();
            }, heartbeat.intervalMs);
        };
        tick();
    }

    /**
     * Stop the heartbeat loop and forget any pending probe.
     */
    private stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer);
            this.heartbeatTimer = undefined;
        }
        if (this.heartbeatDeadline) {
            clearTimeout(this.heartbeatDeadline);
            this.heartbeatDeadline = undefined;
        }
    }

    /**
     * Send a liveness probe now. If no inbound traffic arrives before the heartbeat timeout,
     * the connection is half-open and gets recycled. No-op when no heartbeat is configured,
     * the socket is not open, or a probe is already awaiting its answer.
     */
    private probeConnection() {
        const heartbeat = this.heartbeat;
        const webSocket = this.webSocket;
        if (!heartbeat || this.state !== WebSocketState.OPEN || !webSocket) {
            return;
        }

        if (this.heartbeatDeadline) {
            return;
        }

        try {
            webSocket.send(HEARTBEAT_PING_MESSAGE);
        } catch (error) {
            this.trace?.logger.error('WebSocket heartbeat send failed, recycling:', error);
            this.recycle();
            return;
        }

        this.heartbeatDeadline = setTimeout(() => {
            this.heartbeatDeadline = undefined;
            this.trace?.logger.warning('WebSocket heartbeat timed out, recycling');
            this.recycle();
        }, heartbeat.timeoutMs);
    }

    /**
     * Add a listener for a message from the server.
     */
    public listen(event: 'message', callback: (event: WebSocketMessage) => void): () => void;
    public listen(event: 'state', callback: (state: WebSocketState) => void): () => void;
    public listen(event: 'state' | 'message', callback: (event: any) => void): () => void {
        this.assertNotDestroyed();
        this.events.addListener(event, callback);

        return () => {
            this.events.removeListener(event, callback);
        };
    }

    /**
     * Send a message to the server.
     */
    public send(message: WebSocketMessage) {
        this.assertNotDestroyed();

        if (this.state !== WebSocketState.OPEN || !this.webSocket) {
            if (this.options.queueMessagesWhileClosed ?? true) {
                this.pendingMessages.push(message);
            }
            return;
        }
        this.webSocket.send(message);
    }

    /**
     * Send all pending messages.
     */
    private sendPendingMessages() {
        const webSocket = this.webSocket;
        if (this.state !== WebSocketState.OPEN || !webSocket) {
            return;
        }

        this.pendingMessages.forEach((message) => webSocket.send(message));
        this.pendingMessages = [];
    }

    /**
     * Update the state of the socket.
     */
    private setState(state: WebSocketState) {
        if (this.state === state) {
            return;
        }

        this.trace?.logger.info('socket changing state from', this.state, 'to', state);

        this.state = state;
        this.events.emit('state', state);
    }

    /**
     * Assert that the socket is not destroyed and can still be used.
     */
    private assertNotDestroyed() {
        if (this.destroyed) {
            throw new Error('socket is destroyed');
        }
    }
}

/**
 * https://stackoverflow.com/a/28396165
 */
function getWebSocketCloseReason(event: ws.ICloseEvent): string {
    if (event.code === 1000) {
        return 'Normal closure, meaning that the purpose for which the connection was established has been fulfilled.';
    }
    if (event.code === 1001) {
        return 'An endpoint is "going away", such as a server going down or a browser having navigated away from a page.';
    }
    if (event.code === 1002) {
        return 'An endpoint is terminating the connection due to a protocol error';
    }
    if (event.code === 1003) {
        return 'An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).';
    }
    if (event.code === 1004) {
        return 'Reserved. The specific meaning might be defined in the future.';
    }
    if (event.code === 1005) {
        return 'No status code was actually present.';
    }
    if (event.code === 1006) {
        return 'The connection was closed abnormally, e.g., without sending or receiving a Close control frame';
    }
    if (event.code === 1007) {
        return 'An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [https://www.rfc-editor.org/rfc/rfc3629] data within a text message).';
    }
    if (event.code === 1008) {
        return 'An endpoint is terminating the connection because it has received a message that "violates its policy". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.';
    }
    if (event.code === 1009) {
        return 'An endpoint is terminating the connection because it has received a message that is too big for it to process.';
    }
    if (event.code === 1010) {
        // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
        return `An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: ${event.reason}`;
    }
    if (event.code === 1011) {
        return 'A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.';
    }
    if (event.code === 1015) {
        return "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
    }
    return 'Unknown reason';
}
